import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MapPin, Search, Navigation, Loader2, X } from "lucide-react";

const GOOGLE_MAPS_API_KEY = "AIzaSyCmX43q6uKe_2GmnVq8hQTi9WV4RrpSeRw";

const LocationPicker = ({ onLocationSelect, onClose, initialLocation = null, title = "Select Location", locationLabel = "Delivering to" }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || {
    lat: 12.9716,
    lng: 77.5946,
    address: ""
  });
  const [loading, setLoading] = useState(true);
  const [gettingCurrentLocation, setGettingCurrentLocation] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Load Google Maps Script
  useEffect(() => {
    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => initializeMap();
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
      zoom: 15,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    const marker = new window.google.maps.Marker({
      position: { lat: selectedLocation.lat, lng: selectedLocation.lng },
      map: map,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    // Handle marker drag
    marker.addListener("dragend", () => {
      const position = marker.getPosition();
      const lat = position.lat();
      const lng = position.lng();
      reverseGeocode(lat, lng);
    });

    // Handle map click
    map.addListener("click", (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      marker.setPosition({ lat, lng });
      reverseGeocode(lat, lng);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;
    setLoading(false);

    // Get address for initial location
    if (initialLocation) {
      reverseGeocode(initialLocation.lat, initialLocation.lng);
    }
  }, [selectedLocation.lat, selectedLocation.lng, initialLocation]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });
      
      if (response.results && response.results[0]) {
        const address = response.results[0].formatted_address;
        setSelectedLocation({
          lat,
          lng,
          address,
          placeId: response.results[0].place_id
        });
      }
    } catch (error) {
      console.error("Reverse geocode error:", error);
      setSelectedLocation(prev => ({ ...prev, lat, lng }));
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !window.google) return;

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: searchQuery,
        componentRestrictions: { country: "in" },
        types: ["geocode", "establishment"],
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
          setShowResults(true);
        }
      }
    );
  };

  const handleSelectSearchResult = (prediction) => {
    const placesService = new window.google.maps.places.PlacesService(mapInstanceRef.current);
    
    placesService.getDetails(
      { placeId: prediction.place_id, fields: ["geometry", "formatted_address"] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(17);
          markerRef.current.setPosition({ lat, lng });
          
          setSelectedLocation({
            lat,
            lng,
            address: place.formatted_address,
            placeId: prediction.place_id
          });
          
          setSearchQuery(prediction.description);
          setShowResults(false);
        }
      }
    );
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setGettingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        mapInstanceRef.current?.setCenter({ lat, lng });
        mapInstanceRef.current?.setZoom(17);
        markerRef.current?.setPosition({ lat, lng });
        
        reverseGeocode(lat, lng);
        setGettingCurrentLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Unable to get your location. Please search or drag the pin.");
        setGettingCurrentLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleConfirmLocation = () => {
    if (selectedLocation.lat && selectedLocation.lng) {
      onLocationSelect(selectedLocation);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full max-w-lg h-[90vh] sm:h-[80vh] sm:rounded-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 bg-white border-b border-gray-100">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for area, street name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.length > 2) {
                  handleSearch();
                } else {
                  setShowResults(false);
                }
              }}
              className="pl-10 pr-4 h-12 bg-gray-50 border-gray-200"
            />
          </div>
          
          {/* Search Results */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute left-4 right-4 mt-1 bg-white rounded-lg shadow-lg border border-gray-100 max-h-48 overflow-y-auto z-10">
              {searchResults.map((result) => (
                <button
                  key={result.place_id}
                  onClick={() => handleSelectSearchResult(result)}
                  className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
                >
                  <p className="text-sm font-medium text-gray-900 truncate">{result.structured_formatting?.main_text}</p>
                  <p className="text-xs text-gray-500 truncate">{result.structured_formatting?.secondary_text}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <Loader2 className="w-8 h-8 animate-spin text-[#3FA9F5]" />
            </div>
          )}
          <div ref={mapRef} className="w-full h-full" />
          
          {/* Center Pin Indicator */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none">
            <div className="flex flex-col items-center">
              <div className="bg-white px-3 py-1 rounded-full shadow-md text-xs font-medium text-gray-700 mb-1">
                Move pin to adjust
              </div>
            </div>
          </div>

          {/* Current Location Button */}
          <button
            onClick={getCurrentLocation}
            disabled={gettingCurrentLocation}
            className="absolute bottom-4 right-4 bg-white p-3 rounded-full shadow-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {gettingCurrentLocation ? (
              <Loader2 size={20} className="animate-spin text-[#3FA9F5]" />
            ) : (
              <Navigation size={20} className="text-[#3FA9F5]" />
            )}
          </button>
        </div>

        {/* Selected Location & Confirm */}
        <div className="p-4 bg-white border-t border-gray-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin size={20} className="text-[#3FA9F5]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{locationLabel}</p>
              <p className="text-sm text-gray-600 line-clamp-2">
                {selectedLocation.address || "Select a location on the map"}
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleConfirmLocation}
            disabled={!selectedLocation.address}
            className="w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white font-semibold disabled:opacity-50"
          >
            Confirm Location
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
